// Mobile My Tasks: open tasks assigned to you, grouped Today / Overdue / Next / Unscheduled / Later.
import { useMemo } from 'react';
import { ChevronDown, ChevronRight } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { useDb, useMe } from '@/lib/store';
import { useOpenTask, useWorkspaceId } from '@/lib/nav';
import { toggleExpanded, useUi } from '@/lib/ui';
import { TaskStatusIcon } from '@/pages/inbox/InboxRow';
import { WORK_GROUPS, byDueThenPriority, dateRangeLabel, isClosed, taskTrail, workGroup } from '@/pages/home/taskUtils';
import { PriorityFlag } from '@/components/fields/Priority';
import { TopHeader } from './MobileHome';

export function TaskLine({ task }: { task: Task }) {
  const db = useDb((d) => d);
  const openTask = useOpenTask();
  const due = dateRangeLabel(task);
  const trail = taskTrail(db, task);
  return (
    <button type="button" className="m-task" onClick={() => openTask(task.id)}>
      <span className="m-task-icon">
        <TaskStatusIcon db={db} task={task} size={20} />
      </span>
      <span className="m-task-text">
        <span className="m-task-name truncate">{task.name}</span>
        {trail.length > 0 && <span className="m-task-where truncate">{trail[trail.length - 1]}</span>}
      </span>
      {task.priority && task.priority <= 2 && <PriorityFlag priority={task.priority} size={14} />}
      {due.text && <span className={`m-task-due due-${due.tone}`}>{due.text}</span>}
    </button>
  );
}

export function MobileMyTasks() {
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDb((d) => d);
  const expanded = useUi((s) => s.expanded);
  const groups = useMemo(() => {
    const mine = Object.values(db.tasks).filter((t) => t.workspaceId === ws && !t.archived && t.assigneeIds.includes(me) && !isClosed(db, t));
    return WORK_GROUPS.map((g) => ({ ...g, tasks: mine.filter((t) => workGroup(t) === g.key).sort(byDueThenPriority) })).filter((g) => g.tasks.length);
  }, [db, ws, me]);
  return (
    <div className="m-page">
      <TopHeader title="My Tasks" />
      <div className="m-scroll">
        {groups.map((g) => {
          const open = expanded[`m:work:${g.key}`] ?? g.key !== 'later';
          return (
            <section key={g.key} className="m-group">
              <button type="button" className="m-group-head" onClick={() => toggleExpanded(`m:work:${g.key}`, !open)}>
                {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className={`m-group-label work-${g.key}`}>{g.label}</span>
                <span className="m-group-count">{g.tasks.length}</span>
              </button>
              {open && g.tasks.map((t) => <TaskLine key={t.id} task={t} />)}
            </section>
          );
        })}
        {groups.length === 0 && (
          <div className="m-empty">
            <strong>You're all caught up</strong>
            <span>Tasks assigned to you show up here.</span>
          </div>
        )}
      </div>
    </div>
  );
}
